import React from 'react';
import {
  Action, ActionProps, ConfirmModal, FormHandlers, Portal, useModal,
  useNotifications,
} from '@doveit/bricks';
import {
  HireApplicantResponse, JobApplicationSales, JobApplication,
} from '../../../providers/api/dtos';
import { ReferenceType } from '../../../domain/types';
import SimpleModal from '../../../components/simple-modal/SimpleModal';
import HireApplicantForm, { HireApplicantFormModel } from '../../../agent/components/hire-applicant-form/HireApplicantForm';
import { goToDetailPage } from '../../../utils/navigate/utils';
import { hireApplicant } from '../../../providers/api/applicant/applicantProvider';
import { applicantToHire, getInitialValuesFromApplicant } from './mappers';
import { useApplicant } from '../../../applicant/hooks/use-applicant/useApplicant';

interface ChildFnProps {
  loading?: boolean,
  hireApplicant: VoidFunction,
}

export interface HireApplicantActionProps {
  jobApplication: JobApplication,
  size?: ActionProps['size'],
  onSuccess?: (hireResponse: HireApplicantResponse) => void,
  children?: (props: ChildFnProps) => React.ReactNode,
}

export const HIRE_APPLICANT_SUCCESS_MESSAGE = 'Il candidato è stato assunto con successo';
export const HIRE_APPLICANT_ERROR_MESSAGE = 'Non è stato possibile assumere il candidato';

const HireApplicantAction: React.FC<HireApplicantActionProps> = ({
  jobApplication,
  size,
  onSuccess,
  children,
}) => {
  const formRef = React.useRef<FormHandlers>(null) as React.MutableRefObject<FormHandlers>;
  const [isSaving, setIsSaving] = React.useState(false);
  const { data: applicant, error: applicantError } = useApplicant(jobApplication?.applicantId);

  const hireApplicantModal = useModal();
  const { addSuccess, addError } = useNotifications();

  const isSales = React.useMemo(() => jobApplication.applicationType === 'SALES', [jobApplication.applicationType]);

  const openOfferModal = React.useCallback(() => hireApplicantModal.open(), [hireApplicantModal]);
  const closeOfferModal = React.useCallback(() => hireApplicantModal.close(), [hireApplicantModal]);

  const handleSubmit = React.useCallback(async (formValues?: HireApplicantFormModel) => {
    try {
      setIsSaving(true);
      const hiringApplicant = applicantToHire(jobApplication, formValues);
      const hireResponse = await hireApplicant(hiringApplicant);
      onSuccess?.(hireResponse);
      closeOfferModal();
      addSuccess(HIRE_APPLICANT_SUCCESS_MESSAGE);
      setIsSaving(false);

      if (hireResponse.agentId) {
        goToDetailPage(hireResponse.agentId, ReferenceType.AGENT);
      }
    } catch (error) {
      addError(HIRE_APPLICANT_ERROR_MESSAGE);
      setIsSaving(false);
    }
  }, [addError, addSuccess, closeOfferModal, jobApplication, onSuccess]);

  if (!applicant || applicantError) {
    return null;
  }

  return (
    <>
      {
        children
          ? children({ hireApplicant: openOfferModal, loading: isSaving })
          : (
            <Action
              label="Assumi candidato"
              size={size}
              loading={isSaving}
              onClick={openOfferModal}
              aria-label="Azione per assumere il candidato"
            />
          )
      }
      <Portal>

        {!isSales && (
          <ConfirmModal
            isOpen={hireApplicantModal.isOpen}
            title="Assumi candidato"
            aria-label="Modale per confermare l'assunzione del candidato"
            onConfirm={() => handleSubmit()}
            onAbort={hireApplicantModal.close}
          >
            Confermando l&apos;operazione il candidato sarà registrato come assunto e la candidatura verrà archiviata.
          </ConfirmModal>
        )}

        {isSales && (
          <SimpleModal
            {...hireApplicantModal}
            title="Assumi candidato"
            footer={(
              <Action
                label="Assumi candidato"
                loading={isSaving}
                onClick={() => formRef.current.handleSubmit()}
                color="primary"
                emphasis="high"
                aria-label="Azione per confermare l'assunzione del candidato"
              />
            )}
            aria-label="Modale per confermare l'assunzione del candidato"
          >
            <HireApplicantForm
              innerRef={formRef}
              initialValues={getInitialValuesFromApplicant(jobApplication as JobApplicationSales, applicant)}
              onSubmit={handleSubmit}
            />
            {/** TODO handle document upload */}
          </SimpleModal>
        )}
      </Portal>
    </>
  );
};

export default HireApplicantAction;
